import { useEffect, useRef } from 'react';
import { getElectronAPI } from '@/desktop/electronApi';
import type { HardwareCredType } from '@/desktop/types';

/**
 * Keyboard-wedge MSR listener + simple paste/enter UID capture for RFID/fob.
 * When a swipe/token is detected, calls unlockWithHardware.
 */
interface Props {
  enabled?: boolean;
  onUnlock: (type: HardwareCredType, secretRef: string) => void;
  onError?: (message: string) => void;
}

export default function HardwareCredentialHooks({ enabled = true, onUnlock, onError }: Props) {
  const bufferRef = useRef('');
  const lastKeyAt = useRef(0);

  useEffect(() => {
    if (!enabled || !getElectronAPI()) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const now = Date.now();
      if (now - lastKeyAt.current > 400) bufferRef.current = '';
      lastKeyAt.current = now;

      if (e.key === 'Enter') {
        const token = bufferRef.current.trim();
        bufferRef.current = '';
        if (token.length < 4) return;
        e.preventDefault();
        // Heuristic: long track-like strings → MSR; shorter UIDs → RFID
        const type: HardwareCredType = token.length >= 16 || token.includes('=') || token.startsWith('%')
          ? 'msr'
          : 'rfid';
        try {
          onUnlock(type, token);
        } catch (err) {
          onError?.((err as Error)?.message || 'Card unlock failed');
        }
        return;
      }

      if (e.key.length === 1) {
        bufferRef.current += e.key;
        if (bufferRef.current.length > 256) bufferRef.current = '';
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, onUnlock, onError]);

  return null;
}
